import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { TableColumn, TableFilterRow, TableSearchField, useTableControls } from '../table/TableControls';

export interface LookupField {
  key: string;
  label: string;
  required?: boolean;
  type?: 'text' | 'number' | 'boolean' | 'date' | 'reference';
  helperText?: string;
  // 'reference'-only: renders as an Autocomplete against another entity's API.
  referenceApiPath?: string;
  // true: debounced GET `${referenceApiPath}?search=&pageSize=25` returning
  // `{ items: [...] }` (a large searchable catalog, e.g. Product). false/
  // undefined: fetch GET `${referenceApiPath}?includeArchived=false` once,
  // returning a plain array (a small fixed lookup, e.g. Category/GeneStack).
  referenceSearchable?: boolean;
  // Key on the LIST item's own payload holding the resolved display name
  // (e.g. 'productName') — used for the table column and to seed the
  // Autocomplete's current-value label without a refetch. Defaults to `key`.
  displayKey?: string;
  // Key on the LIST item's own payload holding a string[] of extra detail
  // (e.g. 'productActiveIngredientDetails') rendered as a muted second line
  // under the display name in the table — table only, not used in the
  // dialog. For disambiguating near-identical names (e.g. two products with
  // the same name at different concentrations).
  secondaryDisplayKey?: string;
  // 'reference'-only: key on each RAW option returned by referenceApiPath
  // (e.g. 'activeIngredientDetails' from /api/products) holding a string[]
  // of extra detail, shown as a muted second line under each option in the
  // dropdown while picking — same disambiguation purpose as
  // secondaryDisplayKey, but sourced from the reference API's own response
  // rather than the list item being edited.
  referenceSecondaryKey?: string;
  // 'reference'-only: key of another field in this same `fields` array whose
  // current value scopes this field's options (e.g. Farm/Field aliases are
  // scoped per-Business — the Farm/Field picker shouldn't offer every
  // feature in the system). When set, referenceApiPath is only queried once
  // that other field has a value, appended as `?<dependsOnKey>=<value>`, and
  // the Autocomplete is disabled (with a helper prompt) until then. Options
  // are re-fetched whenever the depended-on field's value changes.
  dependsOnKey?: string;
}

interface LookupItem {
  id: string;
  dateCreated: string;
  archivedDate?: string;
  [key: string]: unknown;
}

interface ReferenceOption {
  id: string;
  name: string;
  secondary?: string[];
}

interface SimpleLookupManagementProps {
  title: string;
  icon: React.ReactNode;
  apiPath: string;
  fields: LookupField[];
  entityLabel: string;
  // Default true, preserving existing callers' archive/restore behavior.
  // false: DELETE is permanent (no restore, no Status column/Switch) — for
  // entities nothing else references by FK, like the import alias/map tables.
  softDelete?: boolean;
  // Rendered in the dialog below the fields — self-contained (does its own
  // fetching), so this component stays ignorant of what e.g. "Product" is.
  renderExtra?: (ctx: { formData: Record<string, string>; editingItem: LookupItem | null }) => React.ReactNode;
}

// "Field Alias" -> "field aliases", "Category" -> "categories".
const pluralLower = (label: string): string =>
  (/(s|x|ch|sh)$/i.test(label) ? `${label}es` : /[^aeiou]y$/i.test(label) ? `${label.slice(0, -1)}ies` : `${label}s`).toLowerCase();

const emptyFormData = (fields: LookupField[]): Record<string, string> =>
  Object.fromEntries(fields.map((f) => [f.key, f.type === 'number' ? '1' : f.type === 'boolean' ? 'false' : '']));

// A reference option's second line: a string[] of details, or one string
// (e.g. a description).
const toSecondaryLines = (value: unknown): string[] =>
  Array.isArray(value) ? (value as string[]) : value ? [String(value)] : [];

const SimpleLookupManagement: React.FC<SimpleLookupManagementProps> = ({
  title,
  icon,
  apiPath,
  fields,
  entityLabel,
  softDelete = true,
  renderExtra,
}) => {
  const auth = useAuth();
  const [items, setItems] = useState<LookupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<LookupItem | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>(emptyFormData(fields));
  const [referenceOptions, setReferenceOptions] = useState<Record<string, ReferenceOption[]>>({});
  const referenceDebounceRefs = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});

  const authHeaders = () => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` });

  const fetchItems = async () => {
    try {
      setLoading(true);
      const url = softDelete ? `${apiPath}?includeArchived=${includeArchived}` : apiPath;
      const response = await fetch(url, { headers: authHeaders() });
      if (!response.ok) {
        throw new Error(`Failed to fetch ${pluralLower(entityLabel)}`);
      }
      setItems(await response.json());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.isSignedIn && auth.tokens?.accessToken) {
      fetchItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived, auth.isSignedIn, auth.tokens?.accessToken]);

  // Small fixed lookups (referenceSearchable !== true) are fetched in full
  // once, up front — no need to wait for the user to type.
  useEffect(() => {
    if (!(auth.isSignedIn && auth.tokens?.accessToken)) {
      return;
    }
    fields
      .filter((f) => f.type === 'reference' && !f.referenceSearchable && f.referenceApiPath && !f.dependsOnKey)
      .forEach(async (f) => {
        try {
          const response = await fetch(`${f.referenceApiPath}?includeArchived=false`, { headers: authHeaders() });
          if (!response.ok) return;
          const data = await response.json();
          const list: ReferenceOption[] = (Array.isArray(data) ? data : []).map((d: Record<string, unknown>) => ({
            id: String(d.id),
            name: String(d.name ?? ''),
            secondary: f.referenceSecondaryKey ? toSecondaryLines(d[f.referenceSecondaryKey]) : undefined,
          }));
          setReferenceOptions((prev) => ({ ...prev, [f.key]: list }));
        } catch {
          // best-effort — dropdown just stays empty if this fails
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isSignedIn, auth.tokens?.accessToken]);

  // Fields whose reference options are scoped by another field's current
  // value (see LookupField.dependsOnKey, e.g. Farm/Field scoped by
  // Business) — re-fetched whenever that other field's value changes, and
  // cleared out while it has no value yet.
  const dependentFields = fields.filter((f) => f.type === 'reference' && f.dependsOnKey);
  const dependsOnValues = dependentFields.map((f) => formData[f.dependsOnKey!] ?? '').join('\u0000');

  useEffect(() => {
    if (!openDialog || dependentFields.length === 0) return;
    dependentFields.forEach(async (f) => {
      const parentValue = formData[f.dependsOnKey!];
      if (!parentValue) {
        setReferenceOptions((prev) => ({ ...prev, [f.key]: [] }));
        return;
      }
      try {
        const params = new URLSearchParams({ [f.dependsOnKey!]: parentValue });
        // referenceApiPath may itself already carry a fixed query string
        // (e.g. '/api/spatial-features?category=Farm') — join with '&' in
        // that case rather than producing a second, malformed '?'.
        const separator = f.referenceApiPath!.includes('?') ? '&' : '?';
        const response = await fetch(`${f.referenceApiPath}${separator}${params.toString()}`, { headers: authHeaders() });
        if (!response.ok) return;
        const data = await response.json();
        const rawItems: Record<string, unknown>[] = Array.isArray(data) ? data : data.items ?? [];
        const newOptions: ReferenceOption[] = rawItems.map((d) => ({
          id: String(d.id),
          name: String(d.name ?? ''),
          secondary: f.referenceSecondaryKey ? toSecondaryLines(d[f.referenceSecondaryKey]) : undefined,
        }));
        setReferenceOptions((prev) => ({ ...prev, [f.key]: newOptions }));
        // The current selection may no longer be valid under the new parent
        // value (e.g. a Farm picked before switching Business) — drop it
        // rather than silently submitting a stale id.
        setFormData((prev) =>
          prev[f.key] && !newOptions.some((o) => o.id === prev[f.key]) ? { ...prev, [f.key]: '' } : prev
        );
      } catch {
        // best-effort — dropdown just stays empty if this fails
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, dependsOnValues]);

  const handleReferenceSearchInput = (field: LookupField, value: string) => {
    const existingTimer = referenceDebounceRefs.current[field.key];
    if (existingTimer) clearTimeout(existingTimer);
    referenceDebounceRefs.current[field.key] = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ pageSize: '25' });
        if (value.trim()) params.set('search', value.trim());
        const response = await fetch(`${field.referenceApiPath}?${params.toString()}`, { headers: authHeaders() });
        if (!response.ok) return;
        const data = await response.json();
        const rawItems: Record<string, unknown>[] = Array.isArray(data) ? data : data.items ?? [];
        const newOptions: ReferenceOption[] = rawItems.map((d) => ({
          id: String(d.id),
          name: String(d.name ?? ''),
          secondary: field.referenceSecondaryKey ? toSecondaryLines(d[field.referenceSecondaryKey]) : undefined,
        }));
        setReferenceOptions((prev) => ({ ...prev, [field.key]: newOptions }));
      } catch {
        // best-effort — Autocomplete just shows no results if this fails
      }
    }, 350);
  };

  const handleOpenDialog = (item?: LookupItem) => {
    if (item) {
      setEditingItem(item);
      setFormData(Object.fromEntries(fields.map((f) => [f.key, String(item[f.key] ?? (f.type === 'number' ? '1' : f.type === 'boolean' ? 'false' : ''))])));

      // Seed each reference field's current selection into the options
      // cache so the Autocomplete shows the right label immediately,
      // without waiting for a search/fetch round-trip.
      fields
        .filter((f) => f.type === 'reference')
        .forEach((f) => {
          const id = item[f.key];
          if (!id) return;
          const name = item[f.displayKey ?? f.key];
          const secondary = f.secondaryDisplayKey ? (item[f.secondaryDisplayKey] as string[] | undefined) : undefined;
          setReferenceOptions((prev) => {
            const existing = prev[f.key] ?? [];
            if (existing.some((o) => o.id === String(id))) return prev;
            return { ...prev, [f.key]: [...existing, { id: String(id), name: String(name ?? ''), secondary }] };
          });
        });
    } else {
      setEditingItem(null);
      setFormData(emptyFormData(fields));
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData(emptyFormData(fields));
  };

  const handleSubmit = async () => {
    try {
      const url = editingItem ? `${apiPath}/${editingItem.id}` : apiPath;
      const method = editingItem ? 'PUT' : 'POST';
      const payload = Object.fromEntries(
        fields.map((f) => [
          f.key,
          f.type === 'number'
            ? Number(formData[f.key])
            : f.type === 'boolean'
              ? formData[f.key] === 'true'
              : f.type === 'reference' && !formData[f.key]
                ? null // an optional picker left empty — '' isn't a valid id
                : formData[f.key],
        ])
      );

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Custom validation returns { error: "..." }; ASP.NET's built-in
        // [ApiController] model validation (e.g. MaxLength/Required) returns
        // { errors: { FieldName: ["message"] } } instead — check both shapes.
        const fieldErrors = errorData.errors ? Object.values(errorData.errors).flat() : [];
        throw new Error(errorData.error || fieldErrors.join(' ') || `Failed to save ${entityLabel.toLowerCase()}`);
      }

      handleCloseDialog();
      await fetchItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDelete = async (id: string) => {
    const confirmText = softDelete
      ? `Archive this ${entityLabel.toLowerCase()}?`
      : `Delete this ${entityLabel.toLowerCase()}? This cannot be undone.`;
    if (!window.confirm(confirmText)) return;
    try {
      const response = await fetch(`${apiPath}/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) {
        throw new Error(`Failed to ${softDelete ? 'archive' : 'delete'} ${entityLabel.toLowerCase()}`);
      }
      await fetchItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const response = await fetch(`${apiPath}/${id}/restore`, { method: 'POST', headers: authHeaders() });
      if (!response.ok) {
        throw new Error(`Failed to restore ${entityLabel.toLowerCase()}`);
      }
      await fetchItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const isFormValid = fields.every((f) => !f.required || formData[f.key]?.trim());

  // Search, column filters and paging all run on the loaded list, against
  // each column's displayed text (a reference column's resolved name).
  const columns = useMemo<TableColumn<LookupItem>[]>(
    () =>
      fields.map((f) => ({
        key: f.key,
        label: f.label,
        getValue: (item) => (f.type === 'boolean' ? (item[f.key] ? 'Yes' : 'No') : String(item[f.displayKey ?? f.key] ?? '')),
        filter: f.type === 'reference' || f.type === 'boolean' ? 'select' : 'text',
      })),
    [fields]
  );
  const table = useTableControls(items, columns);

  if (loading && items.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          {icon}
          {title}
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TableSearchField value={table.search} onChange={table.setSearch} placeholder={`Search ${pluralLower(entityLabel)}…`} />
          {softDelete && (
            <FormControlLabel
              control={<Switch checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />}
              label="Show Archived"
            />
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add {entityLabel}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No {pluralLower(entityLabel)} found.
          </Typography>
        </Paper>
      ) : (
        <Paper>
        <TableContainer>
          <Table sx={{ '& .MuiTableCell-root': { py: 1 } }}>
            <TableHead>
              <TableRow>
                {fields.map((f) => (
                  <TableCell key={f.key}>{f.label}</TableCell>
                ))}
                {softDelete && <TableCell>Status</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
              <TableFilterRow columns={columns} state={table} trailingCells={softDelete ? 2 : 1} />
            </TableHead>
            <TableBody>
              {table.filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={fields.length + (softDelete ? 2 : 1)} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No {pluralLower(entityLabel)} match the search and filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {table.pageItems.map((item) => (
                <TableRow key={item.id}>
                  {fields.map((f) => {
                    if (f.type === 'reference') {
                      const secondary = f.secondaryDisplayKey ? item[f.secondaryDisplayKey] : undefined;
                      const secondaryText = Array.isArray(secondary) ? secondary.join(', ') : undefined;
                      return (
                        <TableCell key={f.key}>
                          <Box>{String(item[f.displayKey ?? f.key] ?? '-') || '-'}</Box>
                          {secondaryText && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {secondaryText}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={f.key}>
                        {f.type === 'boolean' ? (item[f.key] ? 'Yes' : 'No') : String(item[f.key] ?? '-') || '-'}
                      </TableCell>
                    );
                  })}
                  {softDelete && (
                    <TableCell>
                      {item.archivedDate ? (
                        <Chip label="Archived" size="small" color="default" />
                      ) : (
                        <Chip label="Active" size="small" color="success" />
                      )}
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {softDelete && item.archivedDate ? (
                      <Tooltip title={`Restore ${entityLabel}`}>
                        <IconButton size="small" onClick={() => handleRestore(item.id)}>
                          <RestoreIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title={`Edit ${entityLabel}`}>
                          <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={softDelete ? `Archive ${entityLabel}` : `Delete ${entityLabel}`}>
                          <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" {...table.paginationProps} />
        </Paper>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? `Edit ${entityLabel}` : `Add New ${entityLabel}`}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {fields.map((f) => {
              if (f.type === 'boolean') {
                return (
                  <FormControlLabel
                    key={f.key}
                    control={
                      <Switch
                        checked={formData[f.key] === 'true'}
                        onChange={(e) => setFormData((prev) => ({ ...prev, [f.key]: String(e.target.checked) }))}
                      />
                    }
                    label={f.helperText ? `${f.label} — ${f.helperText}` : f.label}
                  />
                );
              }

              if (f.type === 'reference') {
                const options = referenceOptions[f.key] ?? [];
                const selected = options.find((o) => o.id === formData[f.key]) ?? null;
                const parentField = f.dependsOnKey ? fields.find((pf) => pf.key === f.dependsOnKey) : undefined;
                const awaitingParent = !!f.dependsOnKey && !formData[f.dependsOnKey];
                return (
                  <Autocomplete
                    key={f.key}
                    options={options}
                    disabled={awaitingParent}
                    getOptionLabel={(o) => o.name || o.id}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={selected}
                    onChange={(_, value) => setFormData((prev) => ({ ...prev, [f.key]: value?.id ?? '' }))}
                    onInputChange={f.referenceSearchable ? (_, value) => handleReferenceSearchInput(f, value) : undefined}
                    filterOptions={f.referenceSearchable ? (opts) => opts : undefined}
                    renderOption={
                      f.referenceSecondaryKey
                        ? (props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              <Box>
                                <Box>{option.name}</Box>
                                {option.secondary && option.secondary.length > 0 && (
                                  <Typography variant="caption" color="text.secondary" component="div">
                                    {option.secondary.join(', ')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )
                        : undefined
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={f.label}
                        required={f.required}
                        helperText={
                          awaitingParent
                            ? `Select ${parentField?.label ?? 'the field above'} first`
                            : f.referenceSecondaryKey && selected?.secondary && selected.secondary.length > 0
                              ? selected.secondary.join(', ')
                              : f.helperText
                        }
                      />
                    )}
                  />
                );
              }

              return (
                <TextField
                  key={f.key}
                  fullWidth
                  label={f.label}
                  type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                  value={formData[f.key] ?? ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  required={f.required}
                  helperText={f.helperText}
                  InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
                />
              );
            })}
            {renderExtra && renderExtra({ formData, editingItem })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!isFormValid}>
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SimpleLookupManagement;
